import React, { useEffect } from "react";
import { HowItWorks, FAQ, ContactForm } from "./OwnerSections";

// -- CONFIG -
const LINKEDIN_URL = "https://www.linkedin.com/in/johnmatsubu/";
const HEADSHOT_URL = "/headshot.jpg";
const BUYER_PROFILE_URL = "/Matsubu-BuyerProfile-2026.pdf";
const GA_MEASUREMENT_ID = "G-DL495WB30V"; // GA4 Measurement ID for jmatsubu.com

// -- GOOGLE ANALYTICS (GA4) -
function initGA() {
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID.includes("XXXX")) return;
  if ((window as any).gtag) return; // already loaded

  const s = document.createElement("script");
  s.async = true;
  s.src = "https://www.googletagmanager.com/gtag/js?id=" + GA_MEASUREMENT_ID;
  document.head.appendChild(s);

  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) { (window as any).dataLayer.push(arguments); }
  (window as any).gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID);
}

function trackPageView(path: string) {
  const gtag = (window as any).gtag;
  if (gtag) gtag("event", "page_view", { page_path: path, page_location: window.location.href });
}

// -- LINKEDIN ICON -
function LinkedInIcon({ size = 18, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ display: "block" }}>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

// -- PUBLIC SITE -
function PublicSite() {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const eyebrow: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.14em",
    textTransform: "uppercase", margin: "0 0 14px", display: "block",
  };

  const sectionHeading: React.CSSProperties = {
    fontSize: "clamp(1.9rem, 4vw, 2.6rem)", fontWeight: 800, color: "#0f172a",
    letterSpacing: "-0.025em", lineHeight: 1.15, margin: "0 0 64px",
  };

  const bioPara: React.CSSProperties = {
    fontSize: 18, lineHeight: 1.85, color: "#334155", margin: "0 0 24px", fontWeight: 400,
  };

  return (
    <div style={{ fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", color:"#0f172a", minHeight:"100vh", background:"white" }}>
      <style>{`html{scroll-behavior:smooth} section{scroll-margin-top:64px} @media (max-width: 640px){ .nav-links{gap:1rem !important} .nav-links button{font-size:13px !important} .nav-links button:nth-child(1), .nav-links button:nth-child(3){display:none} }`}</style>

      {/* NAV */}
      <nav style={{ position:"sticky",top:0,zIndex:100,background:"rgba(255,255,255,0.96)",backdropFilter:"blur(12px)",borderBottom:"1px solid #f1f5f9",padding:"0 2rem" }}>
        <div style={{ maxWidth:1040,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",height:64 }}>
          <div style={{ fontWeight:700,fontSize:15,color:"#0f172a",letterSpacing:"-0.01em" }}>John Matsubu</div>
          <div className="nav-links" style={{ display:"flex",gap:"2rem",alignItems:"center" }}>
            {([["About","about"],["Criteria","criteria"],["Why Me","whyme"],["FAQ","faq"],["Contact","contact"]] as [string,string][]).map(([label,id]) => (
              <button key={id} onClick={()=>scrollTo(id)} style={{ background:"none",border:"none",cursor:"pointer",fontSize:14,color:"#64748b",fontWeight:500,padding:0 }}>{label}</button>
            ))}
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" title="LinkedIn" style={{ display:"flex",alignItems:"center",color:"#0a66c2" }}>
              <LinkedInIcon size={18} color="#0a66c2"/>
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ background:"linear-gradient(135deg, #1e3a5f 0%, #2d5a8e 55%, #1a4a7a 100%)",color:"white",padding:"110px 2rem 120px" }}>
        <div style={{ maxWidth:740,margin:"0 auto",textAlign:"center" }}>
          <div style={{ display:"inline-block",background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:20,padding:"5px 16px",fontSize:11,fontWeight:700,marginBottom:36,letterSpacing:"0.12em",color:"rgba(255,255,255,0.85)" }}>
            ACTIVELY SEARCHING | NORTHERN CALIFORNIA · OPEN TO WEST COAST
          </div>
          <h1 style={{ fontSize:"clamp(2.3rem, 5.5vw, 3.6rem)",fontWeight:800,margin:"0 0 24px",lineHeight:1.08,letterSpacing:"-0.03em" }}>
            Acquiring a Great Business
          </h1>
          <p style={{ fontSize:"clamp(1rem, 2.5vw, 1.15rem)",opacity:0.75,maxWidth:500,margin:"0 auto 56px",lineHeight:1.8,fontWeight:400 }}>
            A technical operator with 15+ years of experience building and running organizations, ready to invest in, lead, and grow your business for the long term.
          </p>

          {/* Stats - no boxes, just numbers floating in space */}
          <div style={{ display:"flex",justifyContent:"center",gap:"3rem",flexWrap:"wrap",marginBottom:56,paddingBottom:48,borderBottom:"1px solid rgba(255,255,255,0.12)" }}>
            {([["15+","Years Experience"],["$500K-$1.5M","Owner Earnings (SDE)"],["SBA Ready","Lender Pre-Qualified"]] as [string,string][]).map(([val,label]) => (
              <div key={label} style={{ textAlign:"center" }}>
                <div style={{ fontSize:"1.75rem",fontWeight:800,lineHeight:1,letterSpacing:"-0.02em" }}>{val}</div>
                <div style={{ fontSize:11,opacity:0.55,marginTop:6,letterSpacing:"0.09em",fontWeight:600 }}>{label.toUpperCase()}</div>
              </div>
            ))}
          </div>

          <div style={{ display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap" }}>
            <button onClick={()=>scrollTo("criteria")} style={{ background:"white",color:"#1e3a5f",border:"none",borderRadius:8,padding:"13px 30px",fontWeight:700,fontSize:14,cursor:"pointer",letterSpacing:"-0.01em" }}>View My Criteria</button>
            <button onClick={()=>scrollTo("contact")} style={{ background:"transparent",color:"white",border:"1px solid rgba(255,255,255,0.35)",borderRadius:8,padding:"13px 30px",fontWeight:600,fontSize:14,cursor:"pointer" }}>Get In Touch</button>
            {BUYER_PROFILE_URL && (
              <a href={BUYER_PROFILE_URL} download style={{ display:"inline-block",background:"rgba(255,255,255,0.12)",color:"white",border:"1px solid rgba(255,255,255,0.35)",borderRadius:8,padding:"13px 30px",fontWeight:600,fontSize:14,cursor:"pointer",textDecoration:"none" }}>Download Buyer Profile</a>
            )}
          </div>
        </div>
      </section>

      {/* ABOUT - open layout, no card boxes */}
      <section id="about" style={{ padding:"120px 2rem",background:"white" }}>
        <div style={{ maxWidth:1040,margin:"0 auto" }}>
          <span style={eyebrow}>About</span>
          <h2 style={sectionHeading}>Operator. Builder. Future Owner.</h2>

          {/* Profile - photo left, bio right, no box */}
          <div style={{ display:"flex",gap:"5rem",alignItems:"flex-start",marginBottom:80,flexWrap:"wrap" }}>
            {/* Left col */}
            <div style={{ flexShrink:0,width:160 }}>
              <div style={{ width:96,height:96,borderRadius:"50%",background:"#e2e8f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,fontWeight:800,color:"#94a3b8",marginBottom:20,overflow:"hidden" }}>
                {HEADSHOT_URL ? <img src={HEADSHOT_URL} alt="John Matsubu" style={{ width:"100%",height:"100%",objectFit:"cover" }}/> : "JM"}
              </div>
              <div style={{ fontWeight:700,fontSize:17,color:"#0f172a",marginBottom:4 }}>John Matsubu</div>
              <div style={{ color:"#64748b",fontSize:13,lineHeight:1.5,marginBottom:16 }}>Technical Operator & Acquirer</div>
              <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginBottom:20 }}>
                {["PhD ChemE","BA Chem & BizEcon","15+ Yrs"].map(tag => (
                  <span key={tag} style={{ background:"#f1f5f9",color:"#475569",fontSize:11,fontWeight:600,padding:"3px 9px",borderRadius:10 }}>{tag}</span>
                ))}
              </div>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" style={{ display:"inline-flex",alignItems:"center",gap:7,color:"#0a66c2",fontSize:13,fontWeight:600,textDecoration:"none" }}>
                <LinkedInIcon size={14} color="#0a66c2"/>LinkedIn
              </a>
            </div>

            {/* Right col - bio text, no box */}
            <div style={{ flex:1,minWidth:260 }}>
              <p style={bioPara}>
                <strong style={{ color:"#0f172a",fontWeight:700 }}>I grew up in a family business.</strong> My parents ran a pool excavation company in the greater Sacramento and Bay Area for 30 years. I spent my summers on their job sites building forms, tying rebar, and running a skid steer when they'd let me. That's where I learned what it takes to keep a crew working, make payroll, and hand a customer something built right. I also learned that a business like that is someone's life's work, not a line on a spreadsheet.
              </p>
              <p style={bioPara}>
                Since then, I've spent 15 years leading R&D labs and pilot plants, including building three research labs from the ground up, and running the people, equipment, and budgets behind them. I hold a PhD in Chemical Engineering and a BA in Chemistry and Business Economics, so I'm as comfortable on a shop floor as I am in a P&L.
              </p>
              <p style={{ ...bioPara, margin:0 }}>
                Now I'm looking to buy one business in Northern California and run it myself for the long term. I'm focused on technical and industrial companies earning roughly $500K–$1.5M a year for their owner, and I'm pre-qualified with an SBA lender, so financing won't hold up a deal. If you're thinking about what comes next for the business you built, I'd like to talk, confidentially and on your timeline.
              </p>
            </div>
          </div>

          {/* Background - thin divider, no background boxes on items */}
          <div style={{ borderTop:"1px solid #e2e8f0",paddingTop:56 }}>
            <span style={eyebrow}>Background</span>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))",gap:"0 80px" }}>
              {[
                "Built 3 research labs from the ground up",
                "Managed complex multi-stakeholder programs",
                "Experience with regulatory compliance & quality systems",
                "Led technical teams through high-stakes projects",
                "Designed and built high-pressure gas systems and high-temperature reactors",
                "Comfortable with both strategy and day-to-day execution",
              ].map((item,i) => (
                <div key={i} style={{ display:"flex",gap:14,alignItems:"flex-start",padding:"18px 0",borderBottom:"1px solid #f1f5f9" }}>
                  <span style={{ color:"#10b981",fontWeight:700,flexShrink:0,fontSize:15,marginTop:1 }}>{"*"}</span>
                  <span style={{ fontSize:15,color:"#475569",lineHeight:1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CRITERIA - clean row layout, no card boxes */}
      <section id="criteria" style={{ padding:"120px 2rem",background:"#fafbfc" }}>
        <div style={{ maxWidth:1040,margin:"0 auto" }}>
          <span style={eyebrow}>Acquisition Criteria</span>
          <h2 style={sectionHeading}>What I'm looking for</h2>

          {/* Metrics as clean horizontal rows */}
          <div style={{ marginBottom:72 }}>
            {[
              ["Owner earnings (SDE)","$500K - $1.5M"],
              ["Location","Northern California, open to West Coast"],
              ["Deal Structure","Full acquisition"],
              ["Financing","SBA + seller financing"],
            ].map(([label,value],i) => (
              <div key={i} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"22px 0",borderBottom:"1px solid #e2e8f0" }}>
                <span style={{ color:"#64748b",fontSize:16,fontWeight:500 }}>{label}</span>
                <span style={{ color:"#0f172a",fontSize:16,fontWeight:700,letterSpacing:"-0.01em" }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Industries */}
          <div style={{ marginBottom:56 }}>
            <span style={eyebrow}>Target Industries</span>
            <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
              {[
                "Industrial Services","Specialty Manufacturing","Labs & Testing",
                "Technical B2B Services","Home & Commercial Services",
                "Environmental Services","Scientific / R&D Adjacent",
              ].map(ind => (
                <span key={ind} style={{ background:"#eff6ff",color:"#1d4ed8",fontSize:13,fontWeight:600,padding:"6px 16px",borderRadius:20 }}>{ind}</span>
              ))}
            </div>
          </div>

          {/* Notes - plain text list, no green box */}
          <div>
            <span style={eyebrow}>Additional Notes</span>
            <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
              {[
                "Prefer businesses where the owner is willing to stay on briefly for transition",
                "Interested in businesses where technical expertise creates a real competitive moat",
                "Willing to relocate for the right business anywhere on the West Coast",
              ].map((note,i) => (
                <div key={i} style={{ display:"flex",gap:16,alignItems:"flex-start" }}>
                  <span style={{ color:"#cbd5e1",fontWeight:700,flexShrink:0,marginTop:3,fontSize:18,lineHeight:1 }}>&mdash;</span>
                  <span style={{ color:"#475569",fontSize:15,lineHeight:1.7 }}>{note}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY ME - editorial grid, no card boxes */}
      <section id="whyme" style={{ padding:"120px 2rem",background:"white" }}>
        <div style={{ maxWidth:1040,margin:"0 auto" }}>
          <span style={eyebrow}>Why work with me</span>
          <h2 style={sectionHeading}>What sets me apart</h2>

          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))",gap:"56px 80px" }}>
            {[
              { icon:"\u{1F52C}", title:"Technical Depth", desc:"PhD in Chemical Engineering with real-world lab operations experience. I understand technical businesses from the inside, not just the spreadsheet." },
              { icon:"\u{1F465}", title:"People-First Leader", desc:"Built teams, developed talent, and managed through change. I know that people are the engine of any great small business." },
              { icon:">>", title:"Strategic & Hands-On", desc:"I'm not a passive investor. I'll be actively involved in operations, bringing both strategic thinking and willingness to get my hands dirty." },
              { icon:"\u{1F4BC}", title:"Financially Ready", desc:"Pre-qualified with an SBA lender and comfortable with seller financing structures. I've done the preparation to move quickly when the right opportunity appears." },
              { icon:"\u{1F3DB}", title:"Legacy Matters to Me", desc:"I'm buying a business to operate and grow it for the long term, not to flip it. Protecting what you've built is a core commitment I take seriously." },
            ].map(({ icon, title, desc }) => (
              <div key={title}>
                <div style={{ fontSize:30,marginBottom:18,lineHeight:1 }}>{icon}</div>
                <h4 style={{ fontSize:17,fontWeight:700,margin:"0 0 10px",color:"#0f172a",letterSpacing:"-0.01em" }}>{title}</h4>
                <p style={{ color:"#64748b",fontSize:15,lineHeight:1.8,margin:0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HowItWorks/>
      <FAQ/>

      {/* CONTACT */}
      <section id="contact" style={{ padding:"120px 2rem",background:"linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",color:"white" }}>
        <div style={{ maxWidth:600,margin:"0 auto",textAlign:"center" }}>
          <span style={{ ...eyebrow,color:"rgba(255,255,255,0.4)" }}>Contact</span>
          <h2 style={{ fontSize:"clamp(1.9rem, 4vw, 2.6rem)",fontWeight:800,margin:"0 0 20px",letterSpacing:"-0.025em",lineHeight:1.15 }}>Let's Connect</h2>
          <p style={{ opacity:0.65,fontSize:17,margin:"0 0 40px",lineHeight:1.8,fontWeight:400 }}>
            Whether you're an owner thinking about what comes next, a broker with a potential fit, or an advisor with a client in mind, I'd like to hear from you. Confidential, low pressure, one conversation at a time.
          </p>

          <div style={{ marginBottom:56 }}><ContactForm/></div>
          <div style={{ fontSize:13,opacity:0.5,marginBottom:24,letterSpacing:"0.08em",textTransform:"uppercase",fontWeight:700 }}>Or reach me directly</div>

          {/* Contact info - no boxes, just clean rows */}
          <div style={{ display:"flex",flexDirection:"column",gap:24,alignItems:"center",marginBottom:48 }}>
            {[
              { label:"john@jmatsubu.com", href:"mailto:john@jmatsubu.com" },
              { label:"805-252-6095", href:"tel:8052526095" },
              { label:"Based in Sunnyvale, CA", href:undefined },
            ].map(({ label, href }) => (
              <div key={label}>
                {href ? (
                  <a href={href} style={{ color:"rgba(255,255,255,0.75)",fontSize:17,textDecoration:"none",fontWeight:500 }}>{label}</a>
                ) : (
                  <span style={{ color:"rgba(255,255,255,0.55)",fontSize:17,fontWeight:400 }}>{label}</span>
                )}
              </div>
            ))}
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" style={{ display:"inline-flex",alignItems:"center",gap:9,color:"rgba(255,255,255,0.75)",fontSize:17,textDecoration:"none",fontWeight:500 }}>
              <LinkedInIcon size={20} color="rgba(255,255,255,0.75)"/>
              Connect on LinkedIn
            </a>
          </div>

          <div style={{ display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap" }}>
            {BUYER_PROFILE_URL && (
              <a href={BUYER_PROFILE_URL} download style={{ display:"inline-block",background:"transparent",color:"white",border:"1px solid rgba(255,255,255,0.35)",fontWeight:600,fontSize:15,padding:"15px 40px",borderRadius:9,textDecoration:"none",letterSpacing:"-0.01em" }}>
                Download Buyer Profile
              </a>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background:"#060e1a",color:"rgba(255,255,255,0.3)",textAlign:"center",padding:"28px 2rem",fontSize:13 }}>
        <div>(c) 2026 John Matsubu  |  Sunnyvale, CA  |  john@jmatsubu.com</div>
        <div style={{ marginTop:6,fontSize:12,opacity:0.7 }}>Actively seeking business acquisitions — Northern California, open to West Coast</div>
        <div style={{ marginTop:4,fontSize:11,opacity:0.35 }}>v1.6</div>
      </footer>
    </div>
  );
}

// -- ROOT APP -
export default function App() {
  useEffect(() => {
    initGA();
    trackPageView("/public-site");
  }, []);

  return <PublicSite/>;
}
